// "View as" (impersonation), ported from swiftMarketing (connectGS3.0 src/lib/impersonation.js + stores/auth.js).
//
// The server decides everything: who the signed-in user may view as (GSDesignImpersonation/Targets, the same
// reach rules as swiftMarketing), and it re-checks X-GSD-Act-As on every call. This only remembers who was picked.
//
// Storage: localStorage, not swiftMarketing's sessionStorage, because the rest of swiftApp (App.vue, authConfig.js,
// api.service.js) already keys the acting user off localStorage `imp_azureid` -> `azure_id`, shared by every tab.
//   imp_azureid      the person's azure id ('' / missing when off) - the existing key, unchanged
//   imp_info         { target, target_kind, target_name, target_label, real_name, via, read_only, ... } for the banner
//   can_impersonate  '1' when the real user has anyone (staff) to view as; drives the profile menu
import axios from 'axios'
import impersonationApi from '@/api/impersonation.service.js'
import apiService from '@/api/api.service.js'

const ID_KEY = 'imp_azureid'
const INFO_KEY = 'imp_info'
const CAN_KEY = 'can_impersonate'

export const ACT_AS_HEADER = 'X-GSD-Act-As'

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT || ''

function read(key) {
  try { return localStorage.getItem(key) || '' } catch (e) { return '' }
}

function write(key, value) {
  try {
    if (value == null || value === '') localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch (e) {
    /* storage blocked: impersonation simply won't survive a reload */
  }
}

/** The person being viewed as, or null. Banner fields come from imp_info (the server's last answer). */
export function getActAs() {
  const target = read(ID_KEY).trim()
  if (!target) return null
  let info = null
  try { info = JSON.parse(read(INFO_KEY) || 'null') } catch (e) { info = null }
  return { ...(info && info.target === target ? info : {}), target }
}

export function setActAs(value) {
  if (!value?.target) {
    write(ID_KEY, null)
    write(INFO_KEY, null)
    write('imp_user_roles', null)
    return
  }
  write(ID_KEY, value.target)
  write(INFO_KEY, JSON.stringify(value))
}

export const clearActAs = () => setActAs(null)

export const canImpersonate = () => read(CAN_KEY) === '1'

const appHome = () => process.env.BASE_URL || '/'

// Everything cached for the acting user. Start/stop reload the app as well, which resets the rest (component state,
// roles, the router) and re-runs App.vue's fetchData with the new person.
function resetUserCaches() {
  apiService.clearApiCache()
  write('user_azure_data', null)
  write('active_user_roles', null)
}

/** Server validates + audits; throws with its message on 403. Reloads the app on success. */
export async function startImpersonation(target) {
  const profile = await impersonationApi.start(target)
  const info = profile?.impersonation || {}
  resetUserCaches()
  setActAs({ ...info, target: info.target || target, target_name: info.target_name || profile?.display_name || '' })
  window.location.assign(appHome())
}

export async function stopImpersonation({ reload = true } = {}) {
  if (getActAs()) await impersonationApi.stop().catch(() => {}) // audit only; never blocks stopping
  clearActAs()
  resetUserCaches()
  if (reload) window.location.assign(appHome())
}

/**
 * Called by App.vue on every sign-in / reload, before it swaps azure_id.
 *  - viewing as someone: GSDesignMe/Get re-checks it (403 impersonation_denied ends it, see the interceptor) and
 *    refreshes the banner details. Returns the person's azure id, or '' when it was refused.
 *  - otherwise: works out whether the real user has any staff to view as. swiftApp has no realtor experience, so
 *    a "Realtor partners"-only list (any LO with linked realtors) doesn't count.
 */
export async function refreshImpersonation() {
  const current = getActAs()
  if (current) {
    try {
      const me = await impersonationApi.me()
      if (me?.impersonation) setActAs({ ...me.impersonation, target: current.target })
      write(CAN_KEY, '1')
    } catch (e) {
      if (e?.status === 403) {
        clearActAs()
        return ''
      }
      // Network / server error: keep going; every call is still checked server-side.
    }
    return getActAs()?.target || ''
  }

  try {
    const res = await impersonationApi.targets({ pageSize: 1 })
    write(CAN_KEY, (res?.groups || []).some(g => g.level !== 'realtors') ? '1' : null)
  } catch (e) {
    write(CAN_KEY, null)
  }
  return ''
}

// Every call to GSWebAPIv2 asks for the person being viewed as, as swiftMarketing's http.js does. Only this app's API
// host gets the header (Swift API, GSAI etc. never see it); { skipActAs: true } in the request config opts out.
// A 403 impersonation_denied means the server no longer allows it (rights changed, or a forged value): end it cleanly.
export function installActAsInterceptors(client = axios) {
  client.interceptors.request.use(cfg => {
    if (cfg.skipActAs || !API_ENDPOINT || !String(cfg.url || '').startsWith(API_ENDPOINT)) return cfg
    const actAs = getActAs()
    if (actAs) cfg.headers[ACT_AS_HEADER] = actAs.target
    return cfg
  })

  client.interceptors.response.use(undefined, error => {
    const res = error?.response
    const errors = res?.data?.errors || res?.data?.Errors || []
    if (res?.status === 403 && getActAs() && Array.isArray(errors) && errors.includes('impersonation_denied')) {
      clearActAs()
      resetUserCaches()
      window.location.assign(appHome())
    }
    return Promise.reject(error)
  })
}
